/**
 * @file BillPoLinksDialog.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-08
 * @description The POs a bill is linked to, changeable on ANY bill — approved,
 *              paid or keyed in Xero (POST /api/bills/:id/link-po). Links are
 *              this system's own: nothing goes to Xero. Linking marks a PO
 *              billed; taking one off puts it back to what it was when no
 *              other bill covers it. A PO already billed is asked about first.
 *              From 2026-10-08 changing links changes no PO's cost: what now
 *              differs comes back (`cost_differences`) and the bill window
 *              offers "Update PO from this bill…" (the user's choice).
 *
 * 1.1.0 (2026-10-07): third-party haulage links (utils/billHaulageLink) — a
 * haulier's bill linked to a PO made in Xero that it carried. Listed under the
 * goods POs, labelled "Haulage for PO-…"; added with "Haulage for a PO"
 * (HaulagePoLinkDialog — renamed 2026-10-08; the supplier's own Xero PO may
 * be chosen there too), or offered when another supplier's Xero-made PO is
 * chosen; taken off like any link. Neither way changes the PO's cost or
 * status.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import BillPoPicker from './BillPoPicker';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { canLinkPos } from '../utils/billLocalEdits';
import { quantityDifferencesMessage } from '../utils/billCostWriteBack';
import HaulagePoLinkDialog from './HaulagePoLinkDialog';
import { haulageForLabel, haulageOfferFrom } from '../utils/billHaulageLink';
import './BillLocalEdits.css';

function LinksBody({ pos, haulage, saving, error, onRemove, onAdd, onAddHaulage, addBlock }) {
  useSlideOutButton([saving, pos.length, haulage.length, addBlock.ok]);
  const rows = [...pos.map(po => ({ po, isHaulage: false })), ...haulage.map(po => ({ po, isHaulage: true }))];
  return (
    <div className="bill-local-body">
      <p className="bill-local-hint">
        Kept in this system only — nothing is sent to Xero. A linked PO is marked billed; one taken off goes back to
        what it was unless another bill covers it.
      </p>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>PO</th>
              <th>Status</th>
              <th>Date</th>
              <th className="currency">Total</th>
              <th className="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="5" className="no-results">No POs are linked to this bill.</td></tr>
            )}
            {rows.map(({ po, isHaulage }) => (
              <tr key={`${isHaulage ? 'h' : 'g'}-${po.id}`}>
                <td>
                  {isHaulage
                    ? <span title={`This bill is the haulage for it${po.contact_name ? ` (${po.contact_name}'s goods)` : ''} — its cost and status are not changed`}>
                        <strong>{haulageForLabel(po)}</strong>{po.contact_name ? ` · ${po.contact_name}` : ''}
                      </span>
                    : <strong>{po.purchase_order_number || `PO #${po.id}`}</strong>}
                </td>
                <td><span className={`status-badge status-${String(po.status || '').toLowerCase()}`}>{po.status || '—'}</span></td>
                <td>{formatDateShort(po.delivery_date || po.date) || '—'}</td>
                <td className="currency">{po.total != null && !isHaulage ? formatCurrency(po.total) : '—'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="btn-icon" data-write-action disabled={saving}
                            onClick={() => onRemove(po, isHaulage)} title={`Take ${po.purchase_order_number || 'this PO'} off the bill`}>
                      <span className="material-symbols-rounded">link_off</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="buttons slideout bill-local-actions">
        <div>
          <button className="btn-slideout" data-write-action onClick={onAdd} disabled={saving || !addBlock.ok}
                  title={addBlock.ok ? 'Choose POs to link' : addBlock.reason}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">add_link</span></div>
            <span className="btn-label">Link POs</span>
          </button>
        </div>
        <div>
          <button className="btn-slideout" data-write-action onClick={onAddHaulage} disabled={saving || !addBlock.ok}
                  title={addBlock.ok ? "This bill is the haulage for a PO made in Xero — another supplier's, or the supplier's own separate haulage bill" : addBlock.reason}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">local_shipping</span></div>
            <span className="btn-label">Haulage for a PO</span>
          </button>
        </div>
      </div>
      {error && <div className="bill-local-error" role="alert">{error}</div>}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.bill — id, account_id, xero_contact_id, supplier_name, status, linked_pos
 * @param {function} props.onClose
 * @param {function} props.onSaved — (response) after the links changed
 */
function BillPoLinksDialog({ bill, onClose, onSaved }) {
  const [pos, setPos] = useState(() => (Array.isArray(bill?.linked_pos) ? bill.linked_pos : []));
  const [haulage, setHaulage] = useState(() => (Array.isArray(bill?.haulage_pos) ? bill.haulage_pos : []));
  const [pickingHaulage, setPickingHaulage] = useState(false);
  // The server's offer: another supplier's PO made in Xero — haulage instead?
  const [offer, setOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  // The server's question: a PO already billed (a partial or second bill?).
  const [question, setQuestion] = useState(null);
  if (!bill) return null;
  const addBlock = canLinkPos(bill);

  // `nextHaulage` omitted keeps the haulage links as they are.
  const send = async (next, confirmations = {}, nextHaulage = null, reason = null) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${bill.id}/link-po`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          purchaseOrderIds: next.map(p => p.id), confirmations,
          ...(nextHaulage ? { haulagePoIds: nextHaulage.map(p => p.id) } : {}),
          ...(reason ? { reason } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.code === 'NEEDS_CONFIRMATION') {
        setQuestion({ next, nextHaulage, message: (data.needsConfirmation || []).map(n => n.message).join(' ') || data.error });
        return;
      }
      const offered = res.status === 409 ? haulageOfferFrom(data) : null;
      if (offered) {
        setOffer({ next, confirmations, ...offered });
        return;
      }
      if (!res.ok || !data.success) {
        const refused = (data.refused || []).map(r => r.message).filter(Boolean).join(' ');
        setError(refused || data.error || 'The POs could not be changed.');
        return;
      }
      setPos(next);
      if (nextHaulage) setHaulage(nextHaulage);
      onSaved && onSaved({
        ...data,
        quantityNote: quantityDifferencesMessage(data.quantity_differences),
        // What now differs on cost — offered as "Update PO from this bill…".
        costDiffers: data.cost_write_back?.cost_differences?.length || 0
      });
    } catch (err) {
      setError(`The POs could not be changed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const ref = bill.supplier_reference || bill.bill_number || `#${bill.id}`;
  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title={`POs on bill ${ref}`}
      size="lg"
      className="bill-local"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Close</span>
            </button>
          </div>
        </div>
      }
    >
      <LinksBody
        pos={pos}
        haulage={haulage}
        saving={saving}
        error={error}
        addBlock={addBlock}
        onRemove={(po, isHaulage) => setConfirmRemove({ ...po, isHaulage })}
        onAdd={() => setPicking(true)}
        onAddHaulage={() => setPickingHaulage(true)}
      />
      <HaulagePoLinkDialog
        isOpen={pickingHaulage}
        billId={bill.id}
        excludeIds={[...pos, ...haulage].map(p => p.id)}
        linking={saving}
        onClose={() => setPickingHaulage(false)}
        onLink={(po, reason) => {
          setPickingHaulage(false);
          send(pos, {}, [...haulage, po], reason);
        }}
      />
      <BillPoPicker
        isOpen={picking}
        supplierId={bill.account_id}
        xeroContactId={bill.xero_contact_id}
        supplierName={bill.supplier_name || bill.account_company_name}
        excludeBillId={bill.id}
        linkedIds={pos.map(p => p.id)}
        onClose={() => setPicking(false)}
        onAdd={(rows) => {
          setPicking(false);
          const have = new Set(pos.map(p => Number(p.id)));
          send([...pos, ...rows.filter(r => !have.has(Number(r.id)))]);
        }}
      />
      <ConfirmDialog
        isOpen={!!confirmRemove}
        title={confirmRemove?.isHaulage ? 'Take the haulage link off?' : 'Take this PO off the bill?'}
        message={confirmRemove?.isHaulage
          ? `This bill is no longer recorded as the haulage for ${confirmRemove?.purchase_order_number || 'the PO'}. The PO's cost and status are not changed. Nothing changes in Xero.`
          : `${confirmRemove?.purchase_order_number || 'The PO'} is taken off this bill, and any line that billed it no longer names it. It goes back to its earlier status unless another bill covers it. Nothing changes in Xero.`}
        confirmText="Take it off"
        cancelText="Keep it"
        onConfirm={() => {
          const po = confirmRemove;
          setConfirmRemove(null);
          if (po.isHaulage) send(pos, {}, haulage.filter(p => Number(p.id) !== Number(po.id)));
          else send(pos.filter(p => Number(p.id) !== Number(po.id)));
        }}
        onCancel={() => setConfirmRemove(null)}
      />
      <ConfirmDialog
        isOpen={!!offer}
        title="Link as third-party haulage?"
        message={`${offer?.message || ''} Link it as third-party haulage?`}
        confirmText="Link as third-party haulage"
        cancelText="No"
        type="info"
        onConfirm={() => {
          const o = offer;
          setOffer(null);
          const moving = o.next.filter(p => o.ids.includes(Number(p.id)));
          send(o.next.filter(p => !o.ids.includes(Number(p.id))), o.confirmations, [...haulage, ...moving]);
        }}
        onCancel={() => setOffer(null)}
      />
      <ConfirmDialog
        isOpen={!!question}
        title="Already billed"
        message={`${question?.message || ''} Link it anyway?`}
        confirmText="Yes, link it"
        cancelText="No"
        onConfirm={() => { const q = question; setQuestion(null); send(q.next, { already_billed: true }, q.nextHaulage); }}
        onCancel={() => setQuestion(null)}
      />
    </Modal>
  );
}

export default BillPoLinksDialog;
